import { useRef } from "react";
import { Budget, BudgetColumn, BudgetViewType } from "./types";
import BudgetCard from "./budgetCard";
import { OverlayTrigger, Tooltip } from "@rio-cloud/rio-uikit";
import EmptyBudgets from "./emptyBudgets";

interface KanbanBoardProps {
  budgets: Budget[];
  viewType: BudgetViewType;
  onViewTypeChange: (viewType: BudgetViewType) => void;
  onViewHistory?: (budgetId: string) => void;
  onAction?: (budgetId: string, action: string) => void;
  persona?: string;
}

const KanbanBoard = ({
  budgets,
  viewType,
  onViewTypeChange,
  onViewHistory,
  onAction,
  persona,
}: KanbanBoardProps) => {
  const columns: { key: BudgetColumn; label: string }[] = [
    { key: "pendente", label: "Pendente de ação" },
    { key: "enviado", label: "Enviado" },
    { key: "concluido", label: "Concluído" },
  ];

  const contentRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);

  const getBudgetsByColumn = (column: BudgetColumn): Budget[] => {
    return budgets.filter((budget) => budget.column === column);
  };

  const pendenteBudgets = getBudgetsByColumn("pendente");
  const enviadoBudgets = getBudgetsByColumn("enviado");
  const concluidoBudgets = getBudgetsByColumn("concluido");
  
  const maxRows = Math.max(
    pendenteBudgets.length,
    enviadoBudgets.length,
    concluidoBudgets.length
  );

  const getBudgetAtRow = (column: BudgetColumn, rowIndex: number): Budget | null => {
    const columnBudgets = getBudgetsByColumn(column);
    return columnBudgets[rowIndex] || null;
  };

  const showQuadros = persona === "CONSULTANT";

  if (budgets.length === 0) {
    return (
      <div className="flex gap-4">
        {showQuadros && (
          <div className="bg-[#ffff] rounded-lg p-4 min-w-[150px] flex flex-col">
            <div className="text-size-16 font-semibold text-gray-900 mb-3">Quadros</div>
            <div className="h-px bg-gray-300 mb-3"></div>
            <div className="flex flex-col gap-1">
              <div
                className={`px-4 py-2.5 rounded-md cursor-pointer text-size-14 transition-colors duration-200 ${
                  viewType === "geral"
                    ? "bg-blue-100 border-l-4 border-blue-500 text-gray-900 font-medium"
                    : "bg-white text-gray-600 border-l-2 border-transparent hover:bg-gray-100"
                }`}
                onClick={() => onViewTypeChange("geral")}
              >
                Geral
              </div>
              <div
                className={`px-4 py-2.5 rounded-md cursor-pointer text-size-14 transition-colors duration-200 ${
                  viewType === "cliente"
                    ? "bg-blue-100 border-l-4 border-blue-500 text-gray-900 font-medium"
                    : "bg-white text-gray-600 border-l-2 border-transparent hover:bg-gray-100"
                }`}
                onClick={() => onViewTypeChange("cliente")}
              >
                Cliente
              </div>
              <div
                className={`px-4 py-2.5 rounded-md cursor-pointer text-size-14 transition-colors duration-200 ${
                  viewType === "fabricante"
                    ? "bg-blue-100 border-l-4 border-blue-500 text-gray-900 font-medium"
                    : "bg-white text-gray-600 border-l-2 border-transparent hover:bg-gray-100"
                }`}
                onClick={() => onViewTypeChange("fabricante")}
              >
                Fabricante
              </div>
            </div>
          </div>
        )}
        <div className="flex-1 bg-white p-3">
          <EmptyBudgets />
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-4">
      {showQuadros && (
        <div className="bg-[#ffff] rounded-lg p-4 min-w-[150px] flex flex-col">
          <div className="text-size-16 font-semibold text-gray-900 mb-3">Quadros</div>
          <div className="h-px bg-gray-300 mb-3"></div>
          <div className="flex flex-col gap-1">
            <div
              className={`px-4 py-2.5 rounded-md cursor-pointer text-size-14 transition-colors duration-200 ${
                viewType === "geral"
                  ? "bg-blue-100 border-l-4 border-blue-500 text-gray-900 font-medium"
                  : "bg-white text-gray-600 border-l-2 border-transparent hover:bg-gray-100"
              }`}
              onClick={() => onViewTypeChange("geral")}
            >
              Geral
            </div>
            <div
              className={`px-4 py-2.5 rounded-md cursor-pointer text-size-14 transition-colors duration-200 ${
                viewType === "cliente"
                  ? "bg-blue-100 border-l-4 border-blue-500 text-gray-900 font-medium"
                  : "bg-white text-gray-600 border-l-2 border-transparent hover:bg-gray-100"
              }`}
              onClick={() => onViewTypeChange("cliente")}
            >
              Cliente
            </div>
            <div
              className={`px-4 py-2.5 rounded-md cursor-pointer text-size-14 transition-colors duration-200 ${
                viewType === "fabricante"
                  ? "bg-blue-100 border-l-4 border-blue-500 text-gray-900 font-medium"
                  : "bg-white text-gray-600 border-l-2 border-transparent hover:bg-gray-100"
              }`}
              onClick={() => onViewTypeChange("fabricante")}
            >
              Fabricante
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 bg-white p-3">
        <div className="relative w-full select-none">
          <div 
            ref={headerRef}
            className="grid grid-cols-3 gap-0"
          >
            {columns.map((column) => (
              <div key={column.key} className="flex-1 px-0">
                <div className="bg-[#C7D1D9] px-4 py-3 flex justify-between items-center" >
                  <h3 className="text-size-14 font-semibold text-gray-900 m-0 uppercase">
                    {column.label}
                  </h3>
                  <OverlayTrigger
                    trigger="click"
                    placement="bottom"
                    overlay={
                      <Tooltip id={`tooltip-${column.key}`} allowOnTouch>
                        <div className="flex flex-col p-2.5 gap-6 max-w-[200px]">
                          <h3 className="text-size-14 font-bold">{column.label}</h3>
                        </div>
                      </Tooltip>
                    }
                  >
                    <span className="cursor-pointer rioglyph rioglyph-info-circle text-gray-500" style={{ fontSize: "18px" }} />
                  </OverlayTrigger>
                </div>
              </div>
            ))}
          </div>

          <div
            ref={contentRef}
            className="overflow-y-auto"
            style={{ maxHeight: "calc(100vh - 400px)" }}
          >
            <div>
              {Array.from({ length: maxRows }).map((_, rowIndex) => (
                <div key={rowIndex} className="grid grid-cols-3 gap-0" style={{ borderBottom: "16px solid #FFFFFF" }}>
                  {columns.map((column) => {
                    const budget = getBudgetAtRow(column.key, rowIndex);
                    return (
                      <div key={`${column.key}-${rowIndex}`} className="p-0">
                        {budget ? (
                          <BudgetCard
                            budget={budget}
                            onViewHistory={onViewHistory}
                            onAction={onAction}
                          />
                        ) : (
                          <div className="p-[10px] !h-auto w-full flex items-center justify-center" style={{ backgroundColor: "#D0DAE2" }}>
                            <div className="bg-[#E5EBF0] border border-[#344054] rounded-lg p-4 w-full h-[190px] flex items-center justify-center">
                              <span className="text-gray-400">-</span>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KanbanBoard;
